/**
 * One candidate location (FR-SEL-012 to 017).
 *
 * EVERY ROW SAYS WHY, NONE IS HIDDEN. Closed, suspended, not set up, and "does
 * not offer this mode" are different facts with different words, because the
 * one a diner can act on (pick another mode) is lost if they collapse into
 * "closed".
 *
 * DRAWN AS `Main.dc.html` (ORDR-164): a card, the name and an Open or Closed
 * badge on one line, then the address and the detail. The name's button is
 * stretched over the card, so the whole card is the 44px+ target, and it is
 * described by the lines beneath it. No brand mark: one app serves one brand, so every row would carry
 * the same logo.
 */

import { useId, type ReactElement } from "react";
import { OPEN_STATE_WORDS, addressLine, distanceLine, formatOpensAt, lastOrdersLine, modeLabel } from "./format.js";
import type { Candidate, Readiness } from "./types.js";

export interface LocationRowProps {
  readonly candidate: Candidate;
  /** The mode the directory was read for, or null for any. */
  readonly mode: string | null;
  /** Undefined until read, null when there is nothing to show. */
  readonly readiness: Readiness | null | undefined;
  readonly isFavourite: boolean;
  readonly onToggleFavourite: (locationNodeId: string) => void;
  /** Null when this entry may not be chosen (a favourite that changed). */
  readonly onChoose: ((candidate: Candidate) => void) | null;
  /** What changed, for a revalidated favourite. */
  readonly note?: string | null;
  readonly onRemove?: (locationNodeId: string) => void;
  readonly testId: string;
}

function readinessLine(readiness: Readiness | null | undefined): string | null {
  if (readiness === null || readiness === undefined) return null;
  if (readiness.state === "QUOTED" && readiness.readyInSeconds !== null) {
    return `Ready in about ${Math.ceil(readiness.readyInSeconds / 60)} min`;
  }
  if (readiness.state === "REFUSED" && readiness.reason === "CAPACITY_ZERO") return "Not taking new orders right now";
  // NOT_QUOTED, and any reason not named here: an estimate's absence is not a closure.
  return null;
}

function stateLines(candidate: Candidate, mode: string | null): string[] {
  if (candidate.modeAvailable === false) {
    const offered = candidate.enabledModes.map(modeLabel).join(", ");
    return [`Does not offer ${modeLabel(mode ?? "")}. Offers ${offered}.`];
  }
  const lines = [OPEN_STATE_WORDS[candidate.openState]];
  if (candidate.openState === "CLOSING_SOON") {
    const lastOrders = lastOrdersLine(candidate, mode);
    if (lastOrders !== null) lines.push(lastOrders);
  }
  if (candidate.openState === "CLOSED" && candidate.nextOpensAt !== null) {
    lines.push(`Opens ${formatOpensAt(candidate.nextOpensAt, candidate.timezone)}`);
  }
  return lines;
}

function reasonLines(candidate: Candidate, mode: string | null): string[] {
  const lines: string[] = [];
  if (candidate.status === "SUSPENDED") lines.push("Not taking orders at the moment.");
  const relevant = candidate.modes.filter((entry) => mode === null || entry.mode === mode);
  if (relevant.some((entry) => entry.reason === "CONFIGURATION_ERROR")) lines.push("Ordering is not set up here yet.");
  for (const entry of relevant) {
    if (entry.closureReason !== "" && !lines.includes(entry.closureReason)) lines.push(entry.closureReason);
  }
  return lines;
}

/** Open and closing soon still take orders; everything else reads closed. */
function badgeState(candidate: Candidate): "open" | "closed" {
  return candidate.status === "ACTIVE" && candidate.openState !== "CLOSED" ? "open" : "closed";
}

const BADGE: Readonly<Record<"open" | "closed", string>> = {
  open: "bg-[var(--color-success-100)] text-[var(--color-success-800)]",
  closed: "bg-[var(--color-neutral-100)] text-[var(--color-neutral-700)]",
};

export function LocationRow({
  candidate,
  mode,
  readiness,
  isFavourite,
  onToggleFavourite,
  onChoose,
  note = null,
  onRemove,
  testId,
}: LocationRowProps): ReactElement {
  const name = candidate.displayName;
  const address = addressLine(candidate);
  const distance = distanceLine(candidate.distanceM);
  const ready = readinessLine(readiness);
  const detailId = useId();
  // The open state is the BADGE (Main.dc.html); a row that does not offer the
  // mode says so in words instead, and carries no badge.
  const lines = stateLines(candidate, mode);
  const stateWord = candidate.modeAvailable === false ? null : (lines[0] ?? null);
  const stateDetail = candidate.modeAvailable === false ? lines : lines.slice(1);
  const state = badgeState(candidate);

  return (
    <li
      data-testid={testId}
      className="relative flex items-start gap-2 rounded-xl border border-[var(--color-neutral-300)] py-3.5 pr-2 pl-4 focus-within:outline-2 focus-within:outline-[var(--color-primary-500)]"
    >
      <div className="flex grow flex-col gap-1">
        <div className="flex items-center gap-2">
          <h3 className="m-0 grow text-lg font-bold">
            {onChoose === null ? (
              name
            ) : (
              <button
                type="button"
                aria-label={`Choose ${name}`}
                aria-describedby={stateWord === null ? detailId : `${detailId}-state ${detailId}`}
                onClick={() => onChoose(candidate)}
                // Stretched over the card: the whole card is the target.
                className="min-h-11 text-left after:absolute after:inset-0 after:rounded-xl after:content-[''] focus-visible:outline-none"
              >
                {name}
              </button>
            )}
          </h3>
          {stateWord !== null && (
            <span
              id={`${detailId}-state`}
              data-testid="location-state"
              data-state={state}
              className={`rounded-full px-2.5 py-0.5 text-[13px] font-semibold ${BADGE[state]}`}
            >
              {stateWord}
            </span>
          )}
        </div>
        <div id={detailId} className="flex flex-col gap-1">
          {address !== "" && <p className="m-0 text-[15px] text-[var(--color-neutral-600)]">{address}</p>}
          {stateDetail.map((line) => (
            <p key={line} className="m-0 text-[15px] text-[var(--color-neutral-700)]">
              {line}
            </p>
          ))}
          {reasonLines(candidate, mode).map((line) => (
            <p key={line} className="m-0 text-[15px] text-[var(--color-neutral-700)]">
              {line}
            </p>
          ))}
          {ready !== null && <p className="m-0 text-[15px] text-[var(--color-neutral-700)]">{ready}</p>}
          <p className="m-0 text-sm text-[var(--color-neutral-600)]">{candidate.enabledModes.map(modeLabel).join(" · ")}</p>
          {distance !== null && <p className="m-0 text-sm text-[var(--color-neutral-600)]">{distance}</p>}
        </div>
        {note !== null && <p role="note">{note}</p>}
        {onRemove !== undefined && (
          <button
            type="button"
            onClick={() => onRemove(candidate.locationNodeId)}
            className="relative z-10 min-h-11 self-start underline"
          >
            Remove
          </button>
        )}
      </div>
      <button
        type="button"
        aria-pressed={isFavourite}
        aria-label={isFavourite ? `Remove ${name} from favourites` : `Add ${name} to favourites`}
        onClick={() => onToggleFavourite(candidate.locationNodeId)}
        // Above the stretched target, so the star is its own control.
        className="relative z-10 min-h-11 min-w-11 text-xl text-[var(--color-primary-700)]"
      >
        {isFavourite ? "★" : "☆"}
      </button>
    </li>
  );
}
